import { useGetLocationsQuery } from "../redux/services/locations/locationsApi";
import { useFetchAll } from "../hooks";
import { Input, Select, Spinner } from "../shared";
import { Button } from "@/shared";
import _ from "lodash";
import { getFormattedTime } from "../utils";
import { useEffect } from "react";
import { PlusIcon } from "@heroicons/react/20/solid";
import { LocationCreateModal } from "@/modals";
import { useState } from "react";
import useForm from "@/hooks/useForm";

interface Location {
  _id: string;
  name: string;
  receivingHours?: {
    open: string;
    close: string;
  };
  address?: {
    fullAddress: string;
  };
}

interface LocationDropdownProps {
  // Note: our shared Select input expects a string for display. Some callers pass
  // the fullAddress string (ex: Legs/LegCreate form). Others may pass the object.
  // We support both, but recommend passing a string for `value`.
  value: Location | string | null;
  onChange: (location: Location) => void;
  id: string;
  label: string;
  clientId: string;
  placeholder?: string;
  size?: "sm" | "md" | "lg";
  /** Controls modal placement when creating a location. */
  createModalDisplayMode?: "center" | "side";
}

interface LocationOption {
  value: Location;
  label: React.ReactNode;
}

const LocationDropdown: React.FC<LocationDropdownProps> = ({
  value,
  onChange,
  id,
  label,
  clientId,
  placeholder,
  size,
  createModalDisplayMode = "center",
}) => {
  const { form } = useForm({});
  const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);
  const [selectKey, setSelectKey] = useState<number>(0); // Force re-render to close dropdown
  const {
    pagination: { pageIndex, pageSize, setPagination },
    search: { handleGlobalSearch, value: searchValue },
    queryResult: { data, isLoading, isFetching },
    filters: { setFilters },
  } = useFetchAll<Location>({
    queryHook: useGetLocationsQuery,
    defaultPageSize: 20,
    prefetch: false,
  });

  useEffect(() => {
    setFilters([{ id: "clientId", value: clientId }]);
  }, [clientId]);

  const locationOptions: LocationOption[] =
    isLoading || isFetching
      ? []
      : (data as any)?.locations?.map((obj: Location) => ({
          value: obj,
          label: (
            <div className="flex flex-col">
              <div className="flex items-center justify-between">
                <div className="text-lg font-bold">{obj.name}</div>
                {obj.receivingHours && (
                  <div className="text-sm font-normal flex">
                    {getFormattedTime(obj.receivingHours.open)} -{" "}
                    {getFormattedTime(obj.receivingHours.close)}
                  </div>
                )}
              </div>
              <div className="text-sm font-normal">
                {obj.address?.fullAddress}
              </div>
            </div>
          ),
        })) || [];

  const handlePageChange = (newPage: number): void => {
    setPagination({
      pageIndex: newPage,
      pageSize: pageSize,
    } as any);
  };

  return (
    <div>
      <div className="col-span-2 sm:col-span-1 xl:col-span-2">
        <Select
          key={selectKey}
          search
          searchKeys={["name"]}
          fullTextSearch={handleGlobalSearch}
          id={id}
          value={value}
          onChange={onChange}
          label={label}
          options={locationOptions}
          placeholder={placeholder}
          size={size}
          renderDropdown={({
            options,
            handleChange,
          }: {
            options: LocationOption[];
            handleChange: (value: Location) => void;
          }) => {
            return (
              <div className="relative">
                <div className="shadow rounded-b max-h-96 bg-white absolute border border-t-0 w-full z-20 flex flex-col">
                  <div className="overflow-y-scroll flex-1">
                    {isLoading || isFetching ? (
                      <div className="flex items-center justify-center p-4 min-h-24">
                        <Spinner size="md" className="mx-auto" />
                      </div>
                    ) : options.length > 0 ? (
                      options.map((option) => (
                        <div
                          key={option.value._id}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => handleChange(option.value)}
                        >
                          {option.label}
                        </div>
                      ))
                    ) : (
                      <span>
                        No location exists with that name,{" "}
                        <Button
                          onClick={() => setCreateModalVisible(true)}
                          type="link"
                        >
                          click here
                        </Button>{" "}
                        to create it.
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between p-2 border-t bg-white sticky bottom-0">
                    <button
                      onClick={() => handlePageChange(pageIndex - 1)}
                      disabled={pageIndex === 0}
                      className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-xs text-gray-500">
                      Page {pageIndex + 1} of {(data as any)?.totalPages}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCreateModalVisible(true)}
                        className="px-3 pl-2 py-1 text-xs font-medium flex items-center text-white bg-primary-600 rounded-md hover:bg-primary-600"
                      >
                        <PlusIcon className="w-4 h-4" /> New
                      </button>
                      <button
                        onClick={() => handlePageChange(pageIndex + 1)}
                        disabled={
                          !(data as any)?.locations?.length ||
                          (data as any)?.locations?.length < pageSize
                        }
                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          }}
        />
      </div>
      {createModalVisible && (
        <LocationCreateModal
          visible={createModalVisible}
          setVisible={setCreateModalVisible}
          clientId={clientId}
          initData={{ name: searchValue }}
          displayMode={createModalDisplayMode}
          form={form}
          onSubmit={(e: { data: Location }) => {
            onChange(e.data);

            // Force Select component to re-render and close dropdown
            setSelectKey((prev) => prev + 1);
          }}
        />
      )}
    </div>
  );
};

export default LocationDropdown;
